import { DEFAULT_CONFIG, INCOME_FINANCIAL_TYPE, DIVIDEND_REINVESTMENT_TRANSTYPES } from './constants'
import { deriveTextSearchFields } from './columnDefs'

const DEFAULT_INCLUDES = {
  financialType: true,
  transactionTypeDetails: true,
  productDetails: true,
  client: true
}

export { DEFAULT_INCLUDES }

export function mergeConfig (config) {
  return {
    ...DEFAULT_CONFIG,
    ...config,
    availableFilters: { ...DEFAULT_CONFIG.availableFilters, ...config?.availableFilters },
    rules: { ...DEFAULT_CONFIG.rules, ...config?.rules },
    defaultSort: config?.defaultSort ?? DEFAULT_CONFIG.defaultSort
  }
}

export function buildActivitySelect (metricGroups) {
  if (!metricGroups?.length) return null
  const select = {}
  for (const group of metricGroups) {
    const useAbs = !!group.useAbsoluteAmount
    for (let i = 0; i < group.metrics.length; i++) {
      const metric = group.metrics[i]
      const key = `${group.id}_${i}`
      const fts = group.financialTypes ?? []
      if (fts.length === 1) {
        select[key] = { op: metric.op, financialType: fts[0], ...(useAbs ? { useAbsoluteAmount: true } : {}) }
      } else if (fts.length > 1) {
        select[key] = { $add: fts.map(ft => ({ op: metric.op, financialType: ft, ...(useAbs ? { useAbsoluteAmount: true } : {}) })) }
      }
    }
  }
  return Object.keys(select).length ? select : null
}

export function parseActivityResult (row, metricGroups) {
  if (!row || !metricGroups?.length) return null
  const result = {}
  for (const group of metricGroups) {
    const values = {}
    for (let i = 0; i < group.metrics.length; i++) {
      values[i] = row[`${group.id}_${i}`] ?? 0
    }
    result[group.id] = values
  }
  return result
}

export function buildScope ({ clientId, scopeType }) {
  if (scopeType === 'firmwide') return [{ type: 'firm' }]
  return [{ type: 'client', ids: clientId ? [clientId] : [] }]
}

export function buildFilters (filterState, hierarchy, activeMetricGroup) {
  const result = {}

  if (activeMetricGroup?.financialTypes?.length) {
    result.financialtype = { op: 'in', value: activeMetricGroup.financialTypes }
    if (activeMetricGroup.financialTypes.includes(INCOME_FINANCIAL_TYPE)) {
      result.pktranstype = { op: 'notIn', value: DIVIDEND_REINVESTMENT_TRANSTYPES }
    }
  } else if (filterState.transactionTypes?.length && hierarchy?.length) {
    const selected = new Set(filterState.transactionTypes)
    const fullGroups = []
    const partialPks = []

    for (const group of hierarchy) {
      const childPks = group.transactionTypes.map(tt => tt.pktranstype)
      const selectedInGroup = childPks.filter(pk => selected.has(pk))
      if (selectedInGroup.length === 0) continue
      if (selectedInGroup.length === childPks.length) {
        fullGroups.push(group.financialTypeId)
      } else {
        partialPks.push(...selectedInGroup)
      }
    }

    if (fullGroups.length) {
      result.financialtype = { op: 'in', value: fullGroups }
    }
    if (partialPks.length) {
      result.pktranstype = { op: 'in', value: partialPks }
    }
  } else if (filterState.transactionTypes?.length) {
    result.pktranstype = { op: 'in', value: filterState.transactionTypes }
  }

  if (filterState.iscustodialcash === 'yes') {
    result.iscustodialcash = [{ value: 1 }]
  } else if (filterState.iscustodialcash === 'no') {
    result.iscustodialcash = [{ value: 0 }]
  }

  if (filterState.transamountMin != null || filterState.transamountMax != null) {
    const parts = []
    if (filterState.transamountMin != null) parts.push({ op: 'gte', value: filterState.transamountMin })
    if (filterState.transamountMax != null) parts.push({ op: 'lte', value: filterState.transamountMax })
    result.transamount = parts
  }

  return result
}

export function buildTextSearch (searchText, columns = []) {
  if (!searchText?.trim()) return undefined
  const val = searchText.trim()
  const fields = deriveTextSearchFields(columns)
  const result = {}
  for (const field of fields) result[field] = val
  return result
}

export function mapSortState (sortState) {
  if (!sortState?.length) return undefined
  const mapped = sortState
    .filter(s => s.id)
    .map(s => ({ field: s.id, dir: s.desc ? 'desc' : 'asc' }))
  return mapped.length ? mapped : undefined
}
